// Config-driven verification page — the VerifyVendor layout (InboxHeader + InboxSplitLayout, indigo
// gradients, Field grids, RemarksHistory, Notes, ActionButtons from the shared status list) with
// everything page-specific supplied by a config from verificationConfigs.jsx. Used for legacy
// verification screens that are "queue → detail → verify/approve" with no custom editing.
//
// Config contract (only `queue`, `itemKey`, `card`, `approve` are required):
//   title, badge, icon, noun                   header / empty-state text
//   queue: { route, params(ctx) }              ctx = { roleId, userId, path, category } (path/category: the inbox item's)
//   itemKey(row)                               unique key of a queue row
//   card: { title(row), subtitle(row), amount(row)?, meta(row)? }
//   searchText(row)                            text the search box matches
//   detail?: { route, params(row, ctx) }       omit when the queue row already has everything
//   aux?: [{ name, route, params(ctx) }]       supporting lookups loaded once, passed to extra()
//   rowAux?: [{ name, route, params(row, d, ctx), when?(row, d) }]  per-row lookups (loaded with the detail)
//   moid(row, detail)                          MOID for the status list / remarks
//   chkAmt?(row, detail)                       amount for approval limits (default 0)
//   remarksKey?(row, detail)                   Remarks.TransactionNo; omit when the SP writes none
//   showReturn: 'Yes' | 'No'                   whether legacy keeps "Return"
//   excludeActions?(row, detail)               extra actions to hide (lower-case types)
//   isReturned?(row) + returnedNotice          returned rows are resubmitted on an entry screen, or…
//   resubmit?: { fields: [{ key, label, type?: 'text'|'textarea'|'date'|'select', options?, required?,
//                readOnly?, filter?: RegExp, maxLength?, show?(values) }], initial(row, d),
//                validate?(values, row, d) → [errors], route, payload(row, d, values, ctx), ok: [literals] }
//                                              …the legacy inline "returned for update" form
//   header: { title(row, d), subtitle(row, d), chips?(row, d) }
//   sections(row, d) → [{ title?, fields: [[label, value, wide?]] }]
//   extra?(row, d, { aux, toast, ext, setExt }) → JSX   custom block rendered after the sections;
//                                              ext is per-row scratch state (reset on selection)
//   beforeAction?(action, row, d, { aux, ext }) → [errors] | Promise<[errors]>  extra checks before verify/approve
//   approve: { route, payload(row, d, { action, note, user, roleId, userId, aux, ext }), ok: [literals] | (status, row, d) → bool,
//              afterOk?(status, row, d, { action }) → Promise }   follow-up after success (e.g. a file download)
//   resubmit.derive?(values) → values          recompute read-only fields (totals) after every edit
//   detail.route / approve.route / resubmit.route may be a function of the row (one screen, several record types);
//   resubmit field options may be a function of aux (lookup-driven dropdowns)
//   resubmit.method?: 'post'                   for update routes the API exposes as HttpPost
//   resubmit field type 'file' (+ accept?)     value is the chosen File (or null); check it in validate()
//   resubmit.afterOk?(status, row, d, values) → Promise  follow-up after a successful update (file upload)
//   resubmit.render?(row, d, { aux, values, setValues }) → JSX  custom editor (e.g. a line-item grid) inside the
//                                              form; setValues(updater) goes through derive like the plain fields
//   detail.select?(data, row) → record          pick the record out of the response (e.g. data[0])
//   detail.method?: 'post'                     detail routes the API exposes as HttpPost (params become the body)
//   detail.list?: true                         select gets the whole list response (default: its first row only)
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Clock, RotateCcw, FileCheck, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchCVQueue,
    fetchCVDetail,
    fetchCVAux,
    submitCV,
    clearCVDetail,
    resetConfigVerification,
    selectCVQueue, selectCVQueueLoading, selectCVQueueError,
    selectCVDetail, selectCVDetailKey, selectCVDetailLoading, selectCVDetailError,
    selectCVAux, selectCVSubmitLoading,
} from '../../slices/accountsSlice/configVerificationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import { VERIFICATION_CONFIGS } from './verificationConfigs';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

export const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const StatusBadge = ({ returned }) => (returned ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const routeOf = (r, row) => (typeof r === 'function' ? r(row) : r);

// ok: list of success literals (default Submitted/Submited) or a predicate for SPs that append data.
const isOk = (ok, status, row, d) => (typeof ok === 'function'
    ? ok(status, row, d)
    : (ok || ['Submitted', 'Submited']).includes(status));

const ConfigVerification = ({ configKey, notificationData, onNavigate }) => {
    const config = VERIFICATION_CONFIGS[configKey];
    const Icon = config?.icon || FileCheck;

    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const userId = userData?.userId || userData?.UID || '';

    const queue = useSelector(selectCVQueue);
    const queueLoading = useSelector(selectCVQueueLoading);
    const queueError = useSelector(selectCVQueueError);
    const detail = useSelector(selectCVDetail);
    const detailKey = useSelector(selectCVDetailKey);
    const detailLoading = useSelector(selectCVDetailLoading);
    const detailError = useSelector(selectCVDetailError);
    const aux = useSelector(selectCVAux);
    const submitLoading = useSelector(selectCVSubmitLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    const [resubmitValues, setResubmitValues] = useState({});
    const [ext, setExt] = useState({});

    const ctx = { roleId, userId, path: notificationData?.NavigationPath || '', category: notificationData?.ModuleCategory || '' };
    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const selectedKey = selectedItem ? String(config.itemKey(selectedItem)) : null;
    const isReturned = !!(selectedItem && config.isReturned && config.isReturned(selectedItem));
    // The row's detail: fetched when the config has a detail route, otherwise the row itself.
    const record = useMemo(() => {
        if (!selectedItem) return null;
        if (!config.detail) return selectedItem;
        if (detailKey !== selectedKey || detail == null) return null;
        return config.detail.select ? config.detail.select(detail, selectedItem) : detail;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem, selectedKey, detailKey, detail]);
    const moid = record ? config.moid(selectedItem, record) : null;

    const loadQueue = () => {
        if (roleId) dispatch(fetchCVQueue({ route: config.queue.route, params: config.queue.params(ctx) }));
    };

    useEffect(() => {
        dispatch(resetConfigVerification());
        dispatch(resetApprovalData());
        setSelectedItem(null);
        loadQueue();
        (config.aux || []).forEach((a) => dispatch(fetchCVAux({ name: a.name, route: a.route, params: a.params ? a.params(ctx) : undefined })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId, configKey]);

    useEffect(() => {
        dispatch(setShowReturnButton(config.showReturn || 'No'));
        return () => {
            dispatch(resetConfigVerification());
            dispatch(resetApprovalData());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configKey]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearCVDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton(config.showReturn || 'No'));
        if (config.detail && (!isReturned || config.resubmit)) {
            dispatch(fetchCVDetail({ route: routeOf(config.detail.route, selectedItem), params: config.detail.params(selectedItem, ctx), method: config.detail.method, list: config.detail.list, key: selectedKey }));
        }
        setComment('');
        setExt({});
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Per-row supporting lookups once the row's record is in.
    useEffect(() => {
        if (!record || !config.rowAux) return;
        config.rowAux
            .filter((a) => !a.when || a.when(selectedItem, record))
            .forEach((a) => dispatch(fetchCVAux({ name: a.name, route: a.route, params: a.params(selectedItem, record, ctx) })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, !!record]);

    // Status list + remarks once the MOID is known (queue row or detail, per config).
    useEffect(() => {
        if (!moid || !roleId || isReturned) return;
        const chkAmt = config.chkAmt ? config.chkAmt(selectedItem, record) || 0 : 0;
        dispatch(fetchStatusList({ MOID: moid, ROID: roleId, ChkAmt: chkAmt }));
        if (config.remarksKey) {
            dispatch(setSelectedMOID(moid));
            dispatch(fetchRemarks({ trno: config.remarksKey(selectedItem, record), moid }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moid, selectedKey]);

    // Prefill the inline resubmit form once the returned row's detail is in.
    useEffect(() => {
        if (!isReturned || !config.resubmit || !record) return;
        const init = config.resubmit.initial(selectedItem, record);
        setResubmitValues(config.resubmit.derive ? config.resubmit.derive(init) : init);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReturned, selectedKey, record]);

    if (!config) {
        return <div className="p-6 text-red-600">Unknown verification page: {configKey}</div>;
    }

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            loadQueue();
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearCVDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!record) { toast.error(`No ${config.noun || 'item'} selected`); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }
        if (config.beforeAction) {
            const errors = await config.beforeAction(action.type, selectedItem, record, { aux, ext });
            if (errors && errors.length) { errors.forEach((m) => toast.error(m)); return; }
        }

        const actionValue = action.type;
        const payload = config.approve.payload(selectedItem, record, {
            action: actionValue, note: comment.trim(), user: getCurrentUser(), roleId, userId, aux, ext,
        });

        try {
            const result = await dispatch(submitCV({ route: routeOf(config.approve.route, selectedItem), payload })).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (isOk(config.approve.ok, status, selectedItem, record)) {
                if (config.approve.afterOk) {
                    try { await config.approve.afterOk(status, selectedItem, record, { action: actionValue }); } catch (e) { toast.error(`${actionValue} done, but the follow-up step failed: ${e?.message || e}`); }
                }
                toast.success(`${config.successLabel || config.title} ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred');
        }
    };

    const handleResubmit = async () => {
        const rs = config.resubmit;
        if (!rs || !record) return;
        const visible = rs.fields.filter((f) => !f.show || f.show(resubmitValues));
        const errors = visible.filter((f) => f.required && !String(resubmitValues[f.key] ?? '').trim()).map((f) => `Enter ${f.label}`);
        if (rs.validate) errors.push(...rs.validate(resubmitValues, selectedItem, record));
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = rs.payload(selectedItem, record, resubmitValues, { user: getCurrentUser(), roleId, userId });
        try {
            const result = await dispatch(submitCV({ route: routeOf(rs.route, selectedItem), payload, method: rs.method })).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (isOk(rs.ok, status, selectedItem, record)) {
                if (rs.afterOk) {
                    try { await rs.afterOk(status, selectedItem, record, resubmitValues); } catch (e) { toast.error(`Updated, but the follow-up step failed: ${e?.message || e}`); }
                }
                toast.success(`${config.successLabel || config.title} Updated Successfully`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Update was not applied — the server returned no confirmation.');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to update');
        }
    };

    const renderResubmitField = (f) => {
        if (f.type === 'file') {
            return (
                <div key={f.key}>
                    <label className={labelCls}>{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                    <input
                        type="file"
                        accept={f.accept}
                        className={inputCls}
                        onChange={(e) => { const file = e.target.files?.[0] || null; setResubmitValues((prev) => ({ ...prev, [f.key]: file })); }}
                    />
                </div>
            );
        }
        const value = resubmitValues[f.key] ?? '';
        const onChange = (e) => {
            const v = e.target.value;
            if (f.filter && v !== '' && !f.filter.test(v)) return;
            setResubmitValues((prev) => {
                const next = { ...prev, [f.key]: v };
                return config.resubmit.derive ? config.resubmit.derive(next) : next;
            });
        };
        const common = { className: inputCls, value, onChange, disabled: f.readOnly, maxLength: f.maxLength };
        return (
            <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2 lg:col-span-3' : ''}>
                <label className={labelCls}>{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                {f.type === 'textarea' ? <textarea rows={3} {...common} />
                    : f.type === 'select' ? (
                        <select {...common}>
                            <option value="">-Please Select-</option>
                            {((typeof f.options === 'function' ? f.options(aux) : f.options) || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    )
                        : <input type={f.type === 'date' ? 'date' : 'text'} {...common} />}
            </div>
        );
    };

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {config.resubmit.fields.filter((f) => !f.show || f.show(resubmitValues)).map(renderResubmitField)}
            </div>
            {config.resubmit.render && config.resubmit.render(selectedItem, record, {
                aux,
                values: resubmitValues,
                setValues: (updater) => setResubmitValues((prev) => {
                    const next = typeof updater === 'function' ? updater(prev) : updater;
                    return config.resubmit.derive ? config.resubmit.derive(next) : next;
                }),
            })}
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={submitLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {submitLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const text = (config.searchText ? config.searchText(item) : `${config.card.title(item)} ${config.card.subtitle(item)}`) || '';
        return String(text).toLowerCase().includes(searchQuery.toLowerCase());
    });

    const rowReturned = (item) => !!(config.isReturned && config.isReturned(item));

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{config.card.title(item)}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{config.card.subtitle(item)}</p>
                </div>
                {config.card.amount && (
                    <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{config.card.amount(item)}</span>
                )}
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 truncate">{config.card.meta ? config.card.meta(item) : ''}</span>
                <StatusBadge returned={rowReturned(item)} />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">{config.card.title(item)}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{config.card.subtitle(item)}</span>
            {config.card.meta && <span className="text-gray-500 dark:text-gray-400 truncate max-w-[260px]">{config.card.meta(item)}</span>}
            {config.card.amount && <span className="font-semibold text-gray-900 dark:text-white">{config.card.amount(item)}</span>}
            <span className="ml-auto"><StatusBadge returned={rowReturned(item)} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderSections = () => {
        // Sections whose fields are all conditional-and-absent are dropped.
        const sections = (config.sections ? config.sections(selectedItem, record) : []).filter((sec) => sec.fields.some(Boolean));
        if (!sections.length) return null;
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
                {sections.map((sec, idx) => (
                    <div key={sec.title || idx} className={idx ? 'pt-4 border-t border-gray-200 dark:border-gray-700' : ''}>
                        {sec.title && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{sec.title}</p>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {sec.fields.filter(Boolean).map(([label, value, wide]) => (
                                <Field key={label} label={label} value={value} className={wide ? 'sm:col-span-2 lg:col-span-4' : ''} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this {config.noun || 'item'}</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={submitLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
                excludeActions={config.excludeActions ? config.excludeActions(selectedItem, record) : []}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (isReturned && !config.resubmit) {
            return (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700 flex items-start gap-3">
                    <RotateCcw className="w-5 h-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">{config.card.title(selectedItem)} was returned for update</h3>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">{config.returnedNotice || 'Correct and resubmit it from its entry screen — there is nothing to verify until it is resubmitted.'}</p>
                    </div>
                </div>
            );
        }
        if (detailLoading || (!record && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading {config.noun || 'details'}…</span>
                </div>
            );
        }
        if (detailError || !record) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        const chips = config.header.chips ? config.header.chips(selectedItem, record).filter(Boolean) : [];
        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{config.header.title(selectedItem, record)}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{config.header.subtitle(selectedItem, record)}</p>
                    <div className="flex flex-wrap gap-2">
                        {chips.map((c) => (
                            <span key={c} className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{c}</span>
                        ))}
                        <StatusBadge returned={isReturned} />
                    </div>
                </div>

                {renderSections()}
                {config.extra && config.extra(selectedItem, record, { aux, toast, ext, setExt })}

                {config.remarksKey && (
                    <RemarksHistory
                        isOpen={showRemarksHistory}
                        onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                        remarks={remarks}
                        loading={remarksLoading}
                        title="Approval History"
                    />
                )}

                {isReturned ? renderResubmitForm() : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || config.title} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Icon}
                badgeText={config.badge || config.title}
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: config.searchPlaceholder || 'Search…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue.map((r) => ({ ...r, __key: String(config.itemKey(r)) })),
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: loadQueue,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: `No ${config.noun || 'item'}s pending verification!`,
                        itemKey: '__key',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: config.title,
                        icon: Icon,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: config.detailTitle || 'Details',
                        icon: Icon,
                        selectedTitle: config.title,
                        emptyTitle: `No ${config.noun || 'item'} selected`,
                        emptyMessage: `Select a ${config.noun || 'item'} from the list to view details and take action.`,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default ConfigVerification;
